import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { NoDataFound } from '@shared/components/NoDataFound/NoDataFound';
import InfoTooltip from '@shared/components/InfoTooltip';
import { LoadingSpinner, Select, SearchField } from '@shared/components';
import { Module } from '@modules/admin/types';
import { usePermission } from '@modules/auth/hooks';
import {
  usePriceMappings,
  useCreatePriceMapping,
  useUpdatePriceMapping,
  useDropdownFields,
} from '../hooks/usePlaybook';
import type { PriceMapping } from '../types';
import { IDropdownOptions } from '@shared/interface';
import { useFormDialog } from '@shared/hooks/useDialog';

const PriceMappingTab: React.FC = () => {
  const { canAdd, canUpdate } = usePermission(Module.PLAYBOOK);

  // Fetch price mappings and platform options
  const { data: priceMappings, isLoading, error } = usePriceMappings();
  const { data: dropdownFields } = useDropdownFields('offer');
  const createMutation = useCreatePriceMapping();
  const updateMutation = useUpdatePriceMapping();

  // Extract scope and price options from dropdown fields
  const scopeOptions: IDropdownOptions[] = React.useMemo(() => {
    const scopeField = dropdownFields?.find((f) => f.key === 'scope');
    if (!scopeField?.options) return [];
    return scopeField.options;
  }, [dropdownFields]);

  const priceOptions: IDropdownOptions[] = React.useMemo(() => {
    const priceField = dropdownFields?.find((f) => f.key === 'prices');
    if (!priceField?.options) return [];
    return priceField.options;
  }, [dropdownFields]);

  // Local state
  const dialog = useFormDialog<{ price: number | ''; platform: number | ''; value: string }>({
    price: '',
    platform: '',
    value: '',
  });
  const [searchQuery, setSearchQuery] = useState('');

  // Filter mappings based on search query
  const filteredMappings = useMemo(() => {
    if (!priceMappings) return [];
    if (!searchQuery.trim()) return priceMappings;
    const query = searchQuery.toLowerCase();

    return priceMappings.filter(
      (mapping) =>
        priceOptions
          .find((opt) => opt.optionId === mapping.price)
          ?.label.toLowerCase()
          .includes(query) ||
        mapping.value.toLowerCase().includes(query) ||
        scopeOptions
          .find((opt) => opt.optionId === mapping.platform)
          ?.label.toLowerCase()
          .includes(query),
    );
  }, [priceMappings, searchQuery, scopeOptions, priceOptions]);

  const handleAdd = () => {
    dialog.handleOpen();
  };

  const handleEdit = (mapping: PriceMapping) => {
    dialog.handleOpen({
      price: mapping.price,
      platform: mapping.platform,
      value: mapping.value,
    } as any);
  };

  const isEmptyForm = useMemo(() => {
    return dialog.form.price === '' || dialog.form.platform === '' || !dialog.form.value;
  }, [dialog.form]);

  const handleDialogSubmit = async () => {
    if (isEmptyForm) return;

    try {
      const payload = {
        price: Number(dialog.form.price),
        platform: Number(dialog.form.platform),
        value: dialog.form.value,
      };

      if (dialog.isEditing && dialog.editingItem) {
        // Find the original mapping to get the ID
        const mapping = priceMappings?.find(
          (m) => m.price === dialog.editingItem!.price && m.platform === dialog.editingItem!.platform
        );
        if (mapping) {
          await updateMutation.mutateAsync({
            id: mapping.offer_price_mapping_id,
            payload,
          });
        }
      } else {
        await createMutation.mutateAsync(payload);
      }

      dialog.handleClose();
    } catch (err) {
      console.error('Failed to save price mapping:', err);
      // Dialog stays open on error so user can retry
    }
  };

  const getPlatformLabel = (platformId: number): string => {
    const option = scopeOptions.find((opt) => opt.optionId === platformId);
    return option?.label || platformId.toString();
  };

  const getPriceLabel = (priceId: number): string => {
    const option = priceOptions.find((opt) => opt.optionId === priceId);
    return option?.label || priceId.toString();
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading price mappings..." fullHeight />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load price mappings. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6">Price Mapping</Typography>
        <InfoTooltip title="Map prices to platform-specific values. These mappings are used for offer configurations." />
      </Stack>

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by price, platform, or value..."
            minWidth={300}
          />
          {canAdd && (
            <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
              Add Mapping
            </Button>
          )}
        </Stack>

        {(() => {
          const hasMappings = priceMappings && priceMappings.length > 0;
          const hasFilteredResults = filteredMappings.length > 0;

          if (!hasMappings) {
            return <NoDataFound message='No price mappings added yet. Click "Add Mapping" to get started.' py={4} />;
          }

          if (!hasFilteredResults) {
            return <NoDataFound message="No price mappings match your search." py={4} />;
          }

          return (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Price</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Platform</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Value</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMappings.map((mapping) => (
                    <TableRow key={mapping.offer_price_mapping_id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{getPriceLabel(mapping.price)}</TableCell>
                      <TableCell>{getPlatformLabel(mapping.platform)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                          {mapping.value}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {canUpdate && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEdit(mapping)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          );
        })()}
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialog.open} onClose={dialog.handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography component="span" variant="h6" fontSize={18}>
              {dialog.isEditing ? 'Edit Price Mapping' : 'Add Price Mapping'}
            </Typography>
            <InfoTooltip title="Define how a price value maps to a platform-specific identifier." />
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            <Select
              label="Price"
              value={dialog.form.price}
              onChange={(value) => dialog.updateForm({ price: value as number })}
              options={priceOptions}
              useOptionId
              required
              placeholder="Select price"
            />
            <Select
              label="Platform"
              value={dialog.form.platform}
              onChange={(value) => dialog.updateForm({ platform: value as number })}
              options={scopeOptions}
              useOptionId
              required
              placeholder="Select platform"
            />
            <TextField
              label="Value"
              value={dialog.form.value}
              onChange={(e) => dialog.updateForm({ value: e.target.value })}
              fullWidth
              required
              placeholder="e.g., ggs-playstore-20"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={dialog.handleClose} disabled={createMutation.isPending || updateMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDialogSubmit}
            variant="contained"
            disabled={
              isEmptyForm ||
              createMutation.isPending ||
              updateMutation.isPending
            }
            startIcon={
              createMutation.isPending || updateMutation.isPending ? <CircularProgress size={16} /> : undefined
            }
          >
            {(() => {
              if (createMutation.isPending || updateMutation.isPending) return 'Saving...';
              if (dialog.isEditing) return 'Update';
              return 'Add';
            })()}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PriceMappingTab;
